import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Briefcase, BrainCircuit, Code2, Server, Users, MapPin, Clock,
  CheckCircle2, Loader2, Upload, FileText, X, Sparkles, ArrowRight,
} from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { FormInput } from "@/components/shared/FormInput";
import { PrimaryButton, SecondaryButton } from "@/components/shared/Buttons";
import { useSeo } from "@/hooks/useSeo";
import { CAREERS } from "@/lib/site";
import { submitCareerApplication, isValidIndianMobile, isValidEmail } from "@/lib/api";

const ICONS = { Briefcase, BrainCircuit, Code2, Server, Users };

export default function Career() {
  useSeo({
    title: "Careers",
    description:
      "ASAI is hiring — join our team as a DevOps Trainer, Python Trainer, Data Science Trainer, Coordinator or HR Manager. Apply with your resume to shape the next generation of AI talent.",
    path: "/career",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Careers", path: "/career" },
    ],
  });

  const [active, setActive] = useState(null);

  return (
    <div data-testid="page-career">
      <PageHeader
        overline="Careers"
        titleLines={["We're hiring —", "join the ASAI team"]}
        description="Help us build industry-focused AI education in India. Explore the roles below and apply with your resume — our HR team reviews every application."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="container-asai">
          <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-orange/10 text-orange">
                <Sparkles className="h-5 w-5" />
              </span>
              <div>
                <h2 className="font-heading text-2xl text-navy md:text-3xl">Open positions</h2>
                <p className="text-sm text-navy/60">{CAREERS.length} roles currently open · Based in Bulandshahr, UP</p>
              </div>
            </div>
            <SecondaryButton href="mailto:info@atmasammanschoolofai.com" testId="careers-generic-apply">
              Don't see your role? Email us
            </SecondaryButton>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="career-list">
            {CAREERS.map((job, i) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="group relative flex flex-col gap-4 rounded-2xl border border-navy/10 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-orange/40 hover:shadow-[0_30px_60px_-40px_rgba(6,15,34,0.35)]"
                data-testid={`career-card-${job.id}`}
              >
                <div className="flex items-start justify-between">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-royal/10 text-royal">
                    {(() => { const Icon = ICONS[job.icon] || Briefcase; return <Icon className="h-5 w-5" />; })()}
                  </span>
                  <span className="rounded-full border border-orange/30 bg-orange/10 px-3 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-orange">
                    {job.type}
                  </span>
                </div>

                <div>
                  <h3 className="font-heading text-xl text-navy">{job.title}</h3>
                  <p className="mt-1 text-sm font-medium text-royal">{job.summary}</p>
                </div>

                <p className="text-sm leading-relaxed text-navy/65">{job.description}</p>

                <div className="flex flex-wrap gap-1.5 pt-1">
                  {job.skills.map((s) => (
                    <span key={s} className="rounded-full bg-lightgrey px-2.5 py-1 text-[11px] font-medium text-navy/70">
                      {s}
                    </span>
                  ))}
                </div>

                <div className="mt-auto flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-navy/55">
                  <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-orange" /> {job.experience}</span>
                  <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-orange" /> {job.location}</span>
                </div>

                <button
                  type="button"
                  onClick={() => setActive(job)}
                  data-testid={`career-apply-${job.id}`}
                  className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-orange px-5 py-2.5 text-sm font-semibold text-white transition-all hover:scale-[1.03] hover:shadow-[0_10px_28px_-10px_rgba(255,90,0,0.55)]"
                >
                  Apply Now <ArrowRight className="h-4 w-4" />
                </button>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 rounded-3xl bg-navy p-8 text-white md:p-12">
            <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
              <div className="max-w-xl">
                <h3 className="font-heading text-2xl md:text-3xl">What it's like to work at ASAI</h3>
                <p className="mt-2 text-sm text-white/70 md:text-base">
                  Mission-driven team · Modern tools · Hands-on impact on students' careers · Hybrid flexibility · Learning and growth culture.
                </p>
              </div>
              <PrimaryButton
                href="https://wa.me/917830060800?text=Hi%20ASAI%20team%2C%20I%27d%20like%20to%20learn%20more%20about%20working%20at%20ASAI."
                target="_blank"
                rel="noopener noreferrer"
                testId="careers-whatsapp"
              >
                Chat with HR on WhatsApp
              </PrimaryButton>
            </div>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <ApplyModal job={active} onClose={() => setActive(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------- Modal ----------
function ApplyModal({ job, onClose }) {
  const [form, setForm] = useState({ fullName: "", email: "", mobile: "", message: "" });
  const [resume, setResume] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return setResume(null);
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "docx"].includes(ext)) {
      toast.error("Only PDF or DOCX resumes are allowed.");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Resume must be 5 MB or smaller.");
      e.target.value = "";
      return;
    }
    setResume(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.fullName.trim()) err.fullName = "Please enter your full name.";
    if (!isValidIndianMobile(form.mobile)) err.mobile = "Enter a valid 10-digit Indian mobile number.";
    if (!isValidEmail(form.email)) err.email = "Enter a valid email address.";
    if (!resume) err.resume = "Please upload your resume (PDF or DOCX).";
    setErrors(err);
    if (Object.keys(err).length > 0) return;

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("fullName", form.fullName);
      fd.append("email", form.email);
      fd.append("mobile", form.mobile);
      fd.append("position", job.title);
      fd.append("message", form.message);
      fd.append("consent", "true");
      if (resume) fd.append("resume", resume);
      const res = await submitCareerApplication(fd);
      setResult(res);
      toast.success("Application submitted. Thank you!");
    } catch (ex) {
      toast.error(ex?.message || "Could not submit — please try again.");
      if (ex?.errors) setErrors(ex.errors);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      data-testid="career-apply-modal"
    >
      <motion.div
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 24, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-3xl bg-white p-7 md:p-9"
      >
        <button
          type="button" onClick={onClose} aria-label="Close"
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-navy/60 hover:bg-lightgrey hover:text-navy"
          data-testid="career-modal-close"
        >
          <X className="h-5 w-5" />
        </button>

        {result ? (
          <div className="flex flex-col items-center gap-5 py-6 text-center" data-testid="career-apply-success">
            <CheckCircle2 className="h-14 w-14 text-orange" />
            <div>
              <h3 className="font-heading text-2xl text-navy">Thank you for applying!</h3>
              <p className="mt-1 text-sm text-navy/65">{result.message}</p>
            </div>
            <div className="rounded-2xl bg-lightgrey px-6 py-4 text-sm">
              <div className="text-xs uppercase tracking-wider text-navy/50">Reference number</div>
              <div className="mt-1 font-heading text-lg font-bold text-royal" data-testid="career-apply-reference">
                {result.referenceNumber}
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-full border border-navy/15 px-6 py-2 text-sm text-navy/70 hover:border-orange hover:text-orange"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="mb-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-royal">Apply for</div>
              <h3 className="font-heading text-2xl text-navy">{job.title}</h3>
              <p className="text-sm text-navy/60">{job.summary}</p>
            </div>

            <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4" data-testid="career-apply-form">
              <FormInput label="Full Name *" id="c-name" testId="career-fullName"
                value={form.fullName} onChange={set("fullName")}
                placeholder="Your full name" error={errors.fullName} />
              <FormInput label="Email *" id="c-email" type="email" testId="career-email"
                value={form.email} onChange={set("email")}
                placeholder="you@example.com" error={errors.email} />
              <FormInput label="Mobile Number *" id="c-mobile" testId="career-mobile"
                value={form.mobile} onChange={set("mobile")}
                placeholder="10-digit mobile" error={errors.mobile} />

              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-navy/70">
                  Position
                </label>
                <div className="rounded-lg border border-navy/15 bg-lightgrey px-4 py-3 text-sm text-navy" data-testid="career-position">
                  {job.title}
                </div>
              </div>

              <FormInput as="textarea" rows={3}
                label="Message (optional)" id="c-msg" testId="career-message"
                value={form.message} onChange={set("message")}
                placeholder="Short intro, years of experience, availability…" />

              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-navy/70">
                  Resume Upload * <span className="font-normal normal-case text-navy/50">(PDF or DOCX, up to 5 MB)</span>
                </label>
                <label htmlFor="c-resume" className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed px-4 py-5 text-sm transition-colors ${errors.resume ? "border-destructive" : "border-navy/15 hover:border-orange"}`}>
                  <Upload className="h-5 w-5 text-navy/50" />
                  {resume ? (
                    <span className="flex items-center gap-2 font-medium text-navy">
                      <FileText className="h-4 w-4 text-orange" /> {resume.name}
                    </span>
                  ) : (
                    <span className="text-navy/50">Click to upload your resume</span>
                  )}
                </label>
                <input id="c-resume" type="file" accept=".pdf,.docx" onChange={onFile} className="hidden" data-testid="career-resume" />
                {errors.resume && <span className="text-xs text-destructive">{errors.resume}</span>}
              </div>

              <PrimaryButton
                className="mt-2 justify-center"
                disabled={submitting}
                withArrow={!submitting}
                testId="career-apply-submit"
              >
                {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>) : "Submit Application"}
              </PrimaryButton>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
